import { useMemo, useState } from 'react'
import {
  Box,
  CircularProgress,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
} from '@mui/material'
import { useIntl } from 'react-intl'
import { ConsignmentInfoPanel } from '../../../components/ConsignmentInfoPanel'
import SideModalWrapper from '../../Assets/modals/SideModalWrapper'
import { messages } from '../messages'
import { UnassignedConsignment, UnassignedLoad, UnassignedWaypoint, ConsignmentServiceGroup } from '../types'
import { useGetUnassignedConsignmentLoadsQuery } from '../../../app/redux-fetch/apiOrders'
import ConsignmentContentsSidePanel from './ConsignmentContentsSidePanel'

type ConsignmentServicesSidePanelProps = {
  open: boolean
  onClose: () => void
  consignment: UnassignedConsignment | null
  tenantId: number
  waypoint?: UnassignedWaypoint | null
}

const ConsignmentServicesSidePanel = ({
  open,
  onClose,
  consignment,
  tenantId,
  waypoint,
}: ConsignmentServicesSidePanelProps) => {
  const { formatMessage } = useIntl()

  const { data: loads, isLoading: isLoadingLoads } = useGetUnassignedConsignmentLoadsQuery(
    { tenantId, shipmentBookingId: consignment?.shipmentBookingId ?? 0 },
    { skip: !open || !consignment }
  )

  const [contentsPanelOpen, setContentsPanelOpen] = useState(false)
  const [selectedLoadForContents, setSelectedLoadForContents] = useState<UnassignedLoad | null>(null)

  const handleLoadClick = (load: UnassignedLoad) => {
    setSelectedLoadForContents(load)
    setContentsPanelOpen(true)
  }

  const handleCloseContentsPanel = () => {
    setContentsPanelOpen(false)
    setSelectedLoadForContents(null)
  }

  const serviceGroups = useMemo((): ConsignmentServiceGroup[] => {
    if (!loads) return []

    // If a specific waypoint is provided, only show services for that waypoint
    const waypointsToProcess = waypoint ? [waypoint] : consignment?.waypoints || []
    if (waypointsToProcess.length === 0) return []

    const groupMap = new Map<string, ConsignmentServiceGroup>()

    // Build service groups from waypoints data (properly structured)
    waypointsToProcess.forEach((wp) => {
      if (!wp.services || !wp.locationName) return

      // Each waypoint can have multiple services (comma-separated)
      const waypointServices = wp.services.split(', ').filter(Boolean)

      waypointServices.forEach((serviceName) => {
        const groupKey = `${wp.locationName}-${serviceName}`

        if (!groupMap.has(groupKey)) {
          groupMap.set(groupKey, {
            locationId: 0,
            locationName: wp.locationName!,
            serviceId: 0,
            serviceName,
            loads: [],
          })
        }

        // Find loads that have this service (check if load.services contains this service name)
        const group = groupMap.get(groupKey)!
        loads.forEach((load) => {
          if (!load.services) return

          // Check if this load has this service
          const loadHasService = load.services.toLowerCase().includes(serviceName.toLowerCase())
          if (loadHasService) {
            const existingLoad = group.loads.find((l) => l.loadId === load.loadId)
            if (!existingLoad) {
              group.loads.push({
                loadId: load.loadId,
                trackingNumber: load.trackingNumber ?? formatMessage(messages.notAvailable),
                numberOfLoads: load.quantity,
                loadTypeName: load.loadTypeName ?? formatMessage(messages.notAvailable),
                temperatureRange: load.temperatureRange ?? 'Ambient',
              })
            }
          }
        })
      })
    })

    return Array.from(groupMap.values()).sort((a, b) => {
      const locationCompare = a.locationName.localeCompare(b.locationName)
      if (locationCompare !== 0) return locationCompare
      return a.serviceName.localeCompare(b.serviceName)
    })
  }, [waypoint, consignment?.waypoints, loads, formatMessage])

  if (!consignment) return null

  const hasServices = serviceGroups.length > 0

  const getLoadById = (loadId: number): UnassignedLoad | undefined => {
    return loads?.find((l) => l.loadId === loadId)
  }

  return (
    <>
      <SideModalWrapper
        open={open}
        onClose={onClose}
        headerLabel={formatMessage(messages.services)}
        withBottomBox={false}
        width="62.5%"
      >
        <Box sx={{ p: '32px' }}>
          <ConsignmentInfoPanel
            data={{
              shipperName: consignment.shipperName,
              loadTypeName: consignment.loadTypeName,
              description: consignment.description,
              currencyCode: consignment.currencyCode,
              trackingNumber: consignment.trackingNumber,
              customerReferenceNumber: consignment.referenceNumber,
            }}
            variant="sidePanel"
          />

          {isLoadingLoads ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : hasServices ? (
            serviceGroups.map((group, groupIndex) => (
              <Box key={`${group.locationName}-${group.serviceName}-${groupIndex}`} sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                    backgroundColor: '#f5f5f5',
                    p: 1,
                    borderRadius: 1,
                  }}
                >
                  {formatMessage(messages.serviceAtLocation, {
                    location: group.locationName,
                    service: group.serviceName,
                  })}
                </Typography>

                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{formatMessage(messages.load)}</TableCell>
                        <TableCell>{formatMessage(messages.numberOfLoads)}</TableCell>
                        <TableCell>{formatMessage(messages.loadType)}</TableCell>
                        <TableCell>{formatMessage(messages.tempRange)}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {group.loads.map((load) => (
                        <TableRow key={load.loadId}>
                          <TableCell>
                            <Link
                              component="button"
                              variant="body2"
                              onClick={() => {
                                const fullLoad = getLoadById(load.loadId)
                                if (fullLoad) {
                                  handleLoadClick(fullLoad)
                                }
                              }}
                              sx={{ cursor: 'pointer' }}
                            >
                              {load.trackingNumber}
                            </Link>
                          </TableCell>
                          <TableCell>{load.numberOfLoads}</TableCell>
                          <TableCell>{load.loadTypeName}</TableCell>
                          <TableCell>{load.temperatureRange}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            ))
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                {formatMessage(messages.noServicesAssigned)}
              </Typography>
            </Box>
          )}
        </Box>
      </SideModalWrapper>

      {selectedLoadForContents && (
        <ConsignmentContentsSidePanel
          open={contentsPanelOpen}
          onClose={handleCloseContentsPanel}
          consignment={consignment}
          tenantId={tenantId}
        />
      )}
    </>
  )
}

export default ConsignmentServicesSidePanel
